import { Directive, inject, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { isNumber } from 'lodash'
import { select, Store } from '@ngrx/store'
import { Destroyable, SaveForm, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import {
  selectSignaturePref,
  SignaturePreference,
  UpdateSignaturePreferenceAction,
  UpdateSignaturePreferencePayload,
} from '@tokeet-frontend/signature'

function createTimeBasePref(fb: UntypedFormBuilder) {
  const form = fb.group({
    checked: [],
    days: [0, [Validators.min(0)]],
    hours: [0, [Validators.min(0), Validators.max(23)]],
  })
  return form
}

export function createAccountPrefForm(fb: UntypedFormBuilder) {
  return fb.group({
    sender_name: ['', [Validators.minLength(2), Validators.maxLength(30)]],
    reminder: fb.group({
      user_id: [],
      name: [],
      email: [],
    }),
    sender: fb.group({
      users: [[]],
      sent: [],
      viewed: [],
      signed: [],
      declined: [],
      expired: [],
      failed: [],
      before_expired: createTimeBasePref(fb),
      after_expired: createTimeBasePref(fb),
    }),
    signer: fb.group({
      other_signed: [],
      other_declined: [],
      canceled: [],
      expired: [],
      before_expired: createTimeBasePref(fb),
      after_expired: createTimeBasePref(fb),
    }),
  })
}

@Directive()
export abstract class DocumentsPrefForm extends Destroyable implements OnInit {
  form = createAccountPrefForm(this.fb)

  get reminderForm() {
    return this.form && this.form.get('reminder')
  }

  constructor(
    protected fb: UntypedFormBuilder,
    protected store: Store<any>,
    protected amplitudeService: AmplitudeService
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectSignaturePref), untilDestroy(this)).subscribe((pref) => {
      this.setForm(pref)
    })
  }

  setForm(pref: SignaturePreference) {
    if (!pref) {
      return
    }

    const sender = pref.sender_notify_pref
    const signer = pref.signer_notify_pref
    const reminder = pref.reminder

    this.form.patchValue({
      sender_name: pref.sender_name,
      reminder: {
        ...reminder,
      },
      sender: {
        ...sender,
        before_expired: this.getTimeBasePref(sender && sender.time_before_expired),
        after_expired: this.getTimeBasePref(sender && sender.time_after_expired),
      },
      signer: {
        ...signer,
        before_expired: this.getTimeBasePref(signer && signer.time_before_expired),
        after_expired: this.getTimeBasePref(signer && signer.time_after_expired),
      },
    })
  }

  getTimeBasePref(time: number) {
    if (!isNumber(time)) {
      return {
        checked: false,
      }
    }
    const days = Math.floor(time / (24 * 60 * 60))
    const hours = Math.floor((time % (24 * 60 * 60)) / (60 * 60))

    return {
      checked: true,
      days,
      hours,
    }
  }

  getTimePref(data: { checked: boolean; days: number; hours: number }) {
    if (!data || !data.checked) {
      return null
    }
    return ((data.days || 0) * 24 + (data.hours || 0)) * 3600
  }

  save() {
    this.onSave(this.form)
  }

  @SaveForm()
  onSave(form: UntypedFormGroup) {
    this.amplitudeService.logEvent('document-preferences-save')
    const { sender, signer, reminder, sender_name } = this.form.getRawValue()
    const payload: UpdateSignaturePreferencePayload = {
      sender_name,
      reminder,
      sender_notify_pref: {
        ...sender,
        time_before_expired: this.getTimePref(sender.before_expired),
        time_after_expired: this.getTimePref(sender.after_expired),
      },
      signer_notify_pref: {
        ...signer,
        time_before_expired: this.getTimePref(signer.before_expired),
        time_after_expired: this.getTimePref(signer.after_expired),
      },
    }
    this.store.dispatch(UpdateSignaturePreferenceAction({ payload }))
  }
}
